import { TeContainer, TePlaceholderContainer, TeStep, TeSteps, TeText } from '@topcon/tuicl-react';
import { borderRadiusToPixelString } from '@topcon/tuicl/dist/common/utils';
import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { Section } from '../../../utils/styling';
import { AppRoutes } from '../../../app/routes/utils';
import { ReviewResultContextType } from '../../../providers/ReviewResultContext/review-result.types';
import { ReviewResultContext } from '../../../providers/ReviewResultContext/review-result.context';
import { useRequestBusinessPartner } from '../../../pages/RequestBusinessPartner/businessPartnerRequest.queries';
import { ReviewCompanyCreationRequestStep } from './ReviewCompanyCreationRequestStep';
import ReviewRequestBusinessPartnerAgreementTypeStep from './ReviewRequestBusinessPartnerAgreementTypeStep';
import ReviewRequestBusinessPartnerTermsAndConditionsStep from './ReviewRequestBusinessPartnerTermsAndConditionsStep';

type RequestBusinessPartnerStep = 'companyReview' | 'agreementReview' | 'termsAndConditions';

function RequestBusinessPartnerOverview() {
  const { t } = useTranslation();
  const [step, setStep] = useState<RequestBusinessPartnerStep>('companyReview');
  const [searchParam] = useSearchParams();
  const id = searchParam.get('resource');
  const navigate = useNavigate();
  const { setResult } = use<ReviewResultContextType>(ReviewResultContext);
  const {
    data: requestedBusinessPartner,
    isLoading: isRequestBusinessPartnerLoading,
    refetch: refetchRequestBusinessPartner,
    isError: isRequestBusinessPartnerError,
  } = useRequestBusinessPartner(id);

  if (!id || isRequestBusinessPartnerError) {
    navigate(AppRoutes.Overview);
    return;
  }

  const handleAcceptAgreement = () => {
    setResult('accepted');
  };

  return (
    <TeContainer column grow padding="s" gap="xs" overflow="auto" alignItems="center">
      <Section $width="s" $borderRadius={borderRadiusToPixelString('m')}>
        <TeContainer padding="m" fill grow justifyContent="space-between" alignItems="center">
          <TeText size={20} weight={700}>
            {t('requestBusinessPartner.title')}
          </TeText>
          <TeStepsWrapper>
            <TeStep
              text={t('companyReview')}
              icon={step === 'agreementReview' ? 'check_circle_outline' : 'circle'}
              activeByDefault
              active={step === 'companyReview'}
            />
            <TeStep
              text={t('serviceAgreement.review')}
              icon={step === 'termsAndConditions' ? 'check_circle_outline' : 'circle'}
              active={step === 'agreementReview'}
            />

            <TeStep
              text={t('termsAndConditions')}
              icon="circle"
              active={step === 'termsAndConditions'}
            />
          </TeStepsWrapper>
        </TeContainer>
      </Section>
      <Section $width="s" $borderRadius={borderRadiusToPixelString('m')}>
        {isRequestBusinessPartnerLoading || !requestedBusinessPartner ? (
          <TePlaceholderContainer loader text={t('requestBusinessPartner.states.loading')} />
        ) : (
          <>
            <ReviewCompanyCreationRequestStep
              id={id}
              requestBusinessPartner={requestedBusinessPartner}
              visible={step === 'companyReview'}
              onNextStep={() => setStep('agreementReview')}
              onRefetch={() => refetchRequestBusinessPartner()}
            />
            <ReviewRequestBusinessPartnerAgreementTypeStep
              id={id}
              visible={step === 'agreementReview'}
              agreementTerm={requestedBusinessPartner.serviceAgreementTerms}
              onPreviousStep={() => setStep('companyReview')}
              onNextStep={() => setStep('termsAndConditions')}
            />
            <ReviewRequestBusinessPartnerTermsAndConditionsStep
              id={id}
              visible={step === 'termsAndConditions'}
              onPreviousStep={() => setStep('agreementReview')}
              onNextStep={handleAcceptAgreement}
            />
          </>
        )}
      </Section>
    </TeContainer>
  );
}

const TeStepsWrapper = styled(TeSteps)`
  height: 100%;
`;

export default RequestBusinessPartnerOverview;
